import { computed, nextTick, ref } from 'vue';
import { $api } from '@/services';
import { TutorialNamespace } from '@/services/tutorial.service';
import { TUTORIAL_VIRTUAL_STUDENT_ID } from '@/features/tutorial/virtualStudent';
import router from '@/router';
import store from '@/store';

export type TutorialPhase =
	| 'idle'
	| 'gift'
	| 'welcome'
	| 'tour'
	| 'paused'
	| 'step_success'
	| 'done';

export type TutorialTask = {
	id: number;
	key: string;
	title: string;
	description?: string;
	completed: boolean;
	completed_at?: string;
	position: number;
};

export type TutorialItem = {
	id: number;
	key: string;
	title: string;
	description?: string;
	completed: boolean;
	platform: string;
	position: number;
	tasks: TutorialTask[];
};

const TASK_TITLE_VI: Record<string, string> = {
	add_student: 'Thêm học sinh',
	staff_invitation: 'Mời trị liệu viên vào cơ sở',
	edit_plan_with_ai: 'Lên kế hoạch can thiệp cùng Luca AI',
	edit_goal_library: 'Thêm mục tiêu vào Ngân hàng',
	edit_plan: 'Chỉnh sửa kế hoạch can thiệp',
	create_fee_notice: 'Tạo thông báo học phí',
	collect_tuition: 'Ghi nhận thanh toán học phí',
};

const CREATE_STUDENT_SPOTLIGHTS = [
	{
		targetId: 'tour-menu-record',
		title: "Mở menu 'Quản lý hồ sơ'",
		body: 'Phần này gồm có các trang quản lý hồ sơ nhân sự và hồ sơ học sinh của cơ sở.',
		placement: 'right' as const,
	},
	{
		targetId: 'tour-menu-record_student',
		title: "Vào trang 'Hồ sơ học sinh'",
		body: 'Quản lý hồ sơ học sinh tại đây',
		placement: 'right' as const,
	},
	{
		targetId: 'add-student-btn',
		title: "Bấm 'Thêm học sinh'",
		body: 'Thêm học sinh để bắt đầu quản lý và xây dựng kế hoạch can thiệp.',
		placement: 'bottom' as const,
	},
	{
		targetId: 'add-student-form',
		title: 'Điền thông tin học sinh',
		body: "Điền đầy đủ các thông tin trên và bấm 'Thêm học sinh' để hoàn tất",
		placement: 'left' as const,
	},
];

const INVITE_THERAPIST_SPOTLIGHTS = [
	{
		targetId: 'tour-menu-record_employee',
		title: 'Vào trang “Hồ sơ nhân sự”',
		body: 'Trang quản lý hồ sơ nhân sự của cơ sở.',
		placement: 'right' as const,
	},
	{
		targetId: 'add-employee-btn',
		title: 'Bấm “Thêm tài khoản”',
		body: 'Bạn có thể mời trị liệu viên tham gia vào cơ sở can thiệp dưới nhiều vai trò khác nhau như: quản lý cơ sở, kế toán, trưởng chuyên môn hoặc trị liệu viên/giáo viên để cùng quản lý học sinh và chương trình can thiệp.',
		placement: 'left' as const,
	},
	{
		targetId: 'add-employee-form',
		title: 'Điền thông tin và gửi lời mời',
		body: 'Điền đầy đủ các thông tin trên và bấm “Thêm vào hệ thống và gửi lời mời” để hoàn tất',
		placement: 'left' as const,
	},
];

const VID = TUTORIAL_VIRTUAL_STUDENT_ID;

const CREATE_PLAN_SPOTLIGHTS = [
	{
		targetId: 'tour-menu-plans_data',
		title: 'Chọn “Kế hoạch can thiệp”',
		body: 'Trang quản lý kế hoạch can thiệp của toàn bộ học sinh của cơ sở.',
		placement: 'right' as const,
	},
	{
		targetId: 'tutorial-virtual-student-row',
		title: 'Chọn 1 học sinh trong danh sách',
		body: 'Chọn học sinh để xem kế hoạch can thiệp của trẻ',
		placement: 'bottom' as const,
	},
	{
		targetId: 'tour-tab-current',
		title: 'Kế hoạch hiện tại',
		body: 'Kế hoạch đang được dạy, bao gồm các mục tiêu có thể báo cáo kết quả buổi học trên app Luca Pro.',
		placement: 'bottom' as const,
		action: 'continue' as const,
		path: `/plans/${VID}`,
	},
	{
		targetId: 'tour-tab-future',
		title: 'Kế hoạch dự kiến',
		body: 'Kế hoạch can thiệp của tháng tiếp theo, sau khi Kế hoạch hiện tại kết thúc. KHDK được tạo tự động sau khi hoàn thành báo cáo của tháng của kế hoạch hiện tại.',
		placement: 'bottom' as const,
		action: 'continue' as const,
		path: `/plans/${VID}/future`,
	},
	{
		targetId: 'tour-tab-history',
		title: 'Lịch sử kế hoạch',
		body: 'Hiển thị tất cả các kế hoạch mà học sinh đã từng học, cùng với chi tiết của các kế hoạch',
		placement: 'bottom' as const,
		action: 'continue' as const,
		path: `/plans/${VID}/history-plan`,
	},
	{
		targetId: 'tour-tab-goal-bank',
		title: 'Ngân hàng mục tiêu',
		body: 'Nơi lưu trữ các mục tiêu đã được nhập liệu sẵn bởi Trị liệu viên để dùng cho việc làm Kế hoạch can thiệp cho mỗi bé.',
		placement: 'bottom' as const,
		action: 'continue' as const,
		path: `/plans/${VID}/goal-bank`,
	},
	{
		targetId: 'tour-tab-completed',
		title: 'Mục tiêu hoàn thành',
		body: 'Mục tiêu hoàn thành là mục tiêu được Trị liệu viên chọn “Mục tiêu đã hoàn thành” khi thực hiện báo cáo tháng.',
		placement: 'bottom' as const,
		action: 'continue' as const,
		path: `/plans/${VID}/completed`,
	},
	{
		targetId: 'tour-tab-paused',
		title: 'Mục tiêu tạm dừng',
		body: 'Mục tiêu tạm dừng là mục tiêu được Trị liệu viên chọn “Tạm dừng mục tiêu” khi thực hiện báo cáo tháng.',
		placement: 'bottom' as const,
		action: 'continue' as const,
		path: `/plans/${VID}/stopped-target`,
	},
	{
		targetId: 'tour-tab-all',
		title: 'Tất cả mục tiêu',
		body: 'Nơi hiển thị tất cả các mục tiêu trẻ',
		placement: 'left' as const,
		action: 'continue' as const,
		path: `/plans/${VID}/all`,
	},
	{
		targetId: 'tour-tab-current-select',
		title: 'Chọn thẻ Kế hoạch hiện tại',
		body: 'Làm quen với quy trình tạo kế hoạch hiện tại cho trẻ',
		placement: 'bottom' as const,
		path: `/plans/${VID}`,
	},
	{
		targetId: 'plan-more-btn',
		title: 'Bấm nút 3 chấm dọc, chọn “Chỉnh sửa”',
		body: 'Thêm mục tiêu vào kế hoạch hiện tại để các mục tiêu này có thể báo cáo kết quả hàng ngày trên app Trị liệu viên',
		placement: 'left' as const,
	},
	{
		targetId: 'plan-edit-item',
		title: 'Bấm nút 3 chấm dọc, chọn “Chỉnh sửa”',
		body: 'Thêm mục tiêu vào kế hoạch hiện tại để các mục tiêu này có thể báo cáo kết quả hàng ngày trên app Trị liệu viên',
		placement: 'left' as const,
	},
	{
		targetId: 'recommend-modal',
		title: 'Sử dụng tính năng Gợi ý kế hoạch can thiệp',
		body: 'Tính năng này sẽ sử dụng các thư viện mục tiêu tham khảo của Luca Education để gợi ý các mục tiêu can thiệp phù hợp với mong muốn của bạn, dựa trên các tiêu chí: bộ thư viện (chương trình can thiệp), độ tuổi, và lĩnh vực ưu tiên.',
		placement: 'bottom' as const,
	},
	{
		targetId: 'rec-goal-count',
		title: 'Chọn số mục tiêu tối đa muốn lấy',
		body: 'Bạn cũng có thể tick vào ô Không giới hạn số mục tiêu',
		placement: 'left' as const,
		action: 'continue' as const,
		badge: 1,
		path: `/recommend-goal/${VID}`,
	},
	{
		targetId: 'rec-library-list',
		title: 'Lựa chọn thư viện tham khảo mong muốn để lấy mục tiêu',
		body: 'Bạn có thể mời trị liệu viên tham gia cơ sở để cùng quản lý học sinh và chương trình can thiệp.',
		placement: 'right' as const,
		badge: 2,
	},
	{
		targetId: 'rec-step2-btn',
		title: 'Bấm vào đây để tiếp tục',
		body: 'Bấm nút Bước 2: Lĩnh vực can thiệp để tiếp tục',
		placement: 'left' as const,
		badge: 1,
	},
	{
		targetId: 'rec-priority',
		title: 'Lựa chọn Mức độ ưu tiên cho từng lĩnh vực',
		body: 'Mức 0 sẽ không trả về mục tiêu nào của lĩnh vực đó',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 1,
	},
	{
		targetId: 'rec-age',
		title: 'Lựa chọn Độ tuổi cho lĩnh vực',
		body: 'Thông thường mỗi thư viện chỉ có 1 mức tuổi, nếu có nhiều hơn, bạn có thể lựa chọn mức tuổi phù hợp cho lĩnh vực',
		placement: 'bottom' as const,
		action: 'continue' as const,
		badge: 1,
	},
	{
		targetId: 'rec-step3-btn',
		title: 'Bấm vào đây để tiếp tục',
		body: 'Bấm nút Bước 3: Kiểm tra và hoàn tất để tiếp tục',
		placement: 'left' as const,
		badge: 2,
	},
	{
		targetId: 'rec-edit-goals',
		title: 'Chỉnh sửa nội dung mục tiêu nếu cần',
		body: 'Bấm vào biểu tượng bút chì bên cạnh mục tiêu để chỉnh sửa nội dung mục tiêu nếu cần',
		placement: 'left' as const,
		action: 'continue' as const,
	},
	{
		targetId: 'rec-filter-goals',
		title: 'Kiểm tra và chọn lọc các mục tiêu ưng ý',
		body: 'Bạn có thể trị liệu viên tham gia cơ sở để cùng quản lý học sinh và chương trình can thiệp.',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 1,
	},
	{
		targetId: 'rec-complete-btn',
		title: 'Bấm nút Hoàn thành',
		body: 'Bấm nút “Hoàn thành” để thêm các mục tiêu bên dưới vào Ngân hàng của trẻ',
		placement: 'left' as const,
		badge: 2,
	},
	{
		targetId: 'rec-name-form',
		title: 'Điền tên nhóm mục tiêu và nhấn “Lưu”',
		body: 'Đặt tên nhóm cho các mục tiêu đã chọn để hiển thị trong Ngân hàng mục tiêu',
		placement: 'left' as const,
	},
];

const ADD_GOAL_SPOTLIGHTS = [
	{
		targetId: 'tour-menu-plans_data',
		title: 'Chọn “Kế hoạch can thiệp”',
		body: 'Trang quản lý kế hoạch can thiệp của toàn bộ học sinh của cơ sở.',
		placement: 'right' as const,
	},
	{
		targetId: 'tutorial-virtual-student-row',
		title: 'Chọn 1 học sinh trong danh sách',
		body: 'Chọn học sinh để xem kế hoạch can thiệp của trẻ',
		placement: 'bottom' as const,
	},
	{
		targetId: 'tour-tab-goal-bank',
		title: 'Chọn thẻ Ngân hàng mục tiêu',
		body: 'Nơi lưu trữ các mục tiêu đã được nhập liệu sẵn bởi Trị liệu viên để sử dụng cho việc làm Kế hoạch can thiệp cho mỗi bé.',
		placement: 'bottom' as const,
		path: `/plans/${VID}`,
	},
	{
		targetId: 'bank-more-btn',
		title: 'Bấm nút 3 chấm dọc, chọn “Chỉnh sửa”',
		body: 'Thêm mục tiêu vào Ngân hàng mục tiêu để sử dụng cho việc làm Kế hoạch can thiệp cho mỗi bé',
		placement: 'left' as const,
		path: `/plans/${VID}/goal-bank`,
	},
	{
		targetId: 'bank-edit-item',
		title: 'Bấm nút 3 chấm dọc, chọn “Chỉnh sửa”',
		body: 'Thêm mục tiêu vào Ngân hàng mục tiêu để sử dụng cho việc làm Kế hoạch can thiệp cho mỗi bé',
		placement: 'left' as const,
	},
	{
		targetId: 'bank-lib-list',
		title: 'Chọn thư viện',
		body: 'Chọn thư viện chứa các mục tiêu muốn vào Ngân hàng mục tiêu',
		placement: 'bottom' as const,
		path: `/plans/${VID}/goal-bank/create-by-lib`,
	},
	{
		targetId: 'bank-add-mt',
		title: 'Thêm mục tiêu',
		body: 'Sau đó bấm Thêm MT ở mục tiêu muốn thêm vào ngân hàng mục tiêu',
		placement: 'left' as const,
	},
	{
		targetId: 'bank-tab-new',
		title: 'Chọn thẻ Mục tiêu',
		body: 'Chọn thẻ Mục tiêu mới muốn thêm thành phần loại',
		placement: 'bottom' as const,
	},
	{
		targetId: 'bank-form-skill',
		title: 'Lĩnh vực',
		body: 'Chọn lĩnh vực cho mục tiêu',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 1,
		path: `/plans/${VID}/goal-bank/create`,
	},
	{
		targetId: 'bank-form-content',
		title: 'Nội dung mục tiêu',
		body: 'Nhập nội dung mục tiêu',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 2,
	},
	{
		targetId: 'bank-form-assist',
		title: 'Chọn mức độ hỗ trợ',
		body: 'Chọn mức độ hỗ trợ',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 3,
	},
	{
		targetId: 'bank-form-reason',
		title: 'Cơ sở hợp lý',
		body: 'Nhập cơ sở hợp lý',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 4,
	},
	{
		targetId: 'bank-form-strategy',
		title: 'Chiến lược',
		body: 'Nhập chiến lược',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 5,
	},
	{
		targetId: 'bank-form-process',
		title: 'Quy trình thực hiện',
		body: 'Nhập quy trình thực hiện',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 6,
	},
	{
		targetId: 'bank-form-activity',
		title: 'Hoạt động',
		body: 'Thêm hoạt động cho mục tiêu',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 7,
	},
	{
		targetId: 'bank-form-submit',
		title: 'Hoàn thành',
		body: 'Bấm nút Thêm mục tiêu để hoàn thành',
		placement: 'right' as const,
		badge: 8,
	},
	{
		targetId: 'bank-form-done',
		title: 'Thêm mục tiêu mới thành công',
		body: 'Mục tiêu sẽ hiện thị trong danh sách này. Bạn có thể xoá hoặc chỉnh sửa nội dung mục tiêu tại đây',
		placement: 'right' as const,
		action: 'continue' as const,
	},
];

const PID = 'tutorial-plan-1';

const EDIT_PLAN_SPOTLIGHTS = [
	{
		targetId: 'tour-menu-plans_data',
		title: 'Chọn “Kế hoạch can thiệp”',
		body: 'Trang quản lý kế hoạch can thiệp của toàn bộ học sinh của cơ sở.',
		placement: 'right' as const,
	},
	{
		targetId: 'tutorial-virtual-student-row',
		title: 'Chọn 1 học sinh trong danh sách',
		body: 'Chọn học sinh để xem kế hoạch can thiệp của trẻ',
		placement: 'bottom' as const,
	},
	{
		targetId: 'tour-tab-current-select',
		title: 'Chọn thẻ Kế hoạch hiện tại',
		body: 'Làm quen với quy trình tạo kế hoạch hiện tại cho trẻ',
		placement: 'bottom' as const,
		path: `/plans/${VID}`,
	},
	{
		targetId: 'plan-more-btn',
		title: 'Bấm nút 3 chấm dọc, chọn “Chỉnh sửa”',
		body: 'Thêm mục tiêu vào kế hoạch hiện tại để các mục tiêu này có thể báo cáo kết quả hàng ngày trên app Trị liệu viên',
		placement: 'left' as const,
		path: `/plans/${VID}`,
	},
	{
		targetId: 'plan-edit-item',
		title: 'Bấm nút 3 chấm dọc, chọn “Chỉnh sửa”',
		body: 'Thêm mục tiêu vào kế hoạch hiện tại để các mục tiêu này có thể báo cáo kết quả hàng ngày trên app Trị liệu viên',
		placement: 'left' as const,
	},
	{
		targetId: 'plan-teach-mt',
		title: 'Dạy mục tiêu',
		body: 'Bấm Dạy MT ở mục tiêu muốn thêm mục tiêu vào Kế hoạch hiện tại của trẻ',
		placement: 'left' as const,
		path: `/plans/${VID}/target/${PID}/create-by-bank`,
	},
	{
		targetId: 'plan-select-goals',
		title: 'Chọn nhiều mục tiêu',
		body: 'Tick chọn vào những mục tiêu bạn muốn thêm vào Ngân hàng mục tiêu của trẻ',
		placement: 'bottom' as const,
		action: 'continue' as const,
	},
	{
		targetId: 'plan-add-selected',
		title: 'Thêm nhiều mục tiêu',
		body: 'Bấm nút “Thêm mục tiêu đã chọn” để thêm tất cả các mục tiêu vừa chọn vào Ngân hàng.',
		placement: 'bottom' as const,
	},
	{
		targetId: 'plan-added-success',
		title: 'Thêm mục tiêu mới thành công',
		body: 'Mục tiêu sẽ hiển thị trong danh sách này. Bạn có thể xoá hoặc chỉnh sửa nội dung mục tiêu tại đây',
		placement: 'right' as const,
		action: 'continue' as const,
	},
	{
		targetId: 'plan-tab-new',
		title: 'Chọn thẻ Mục tiêu mới',
		body: 'Chọn thẻ Mục tiêu mới trên thanh phân loại',
		placement: 'bottom' as const,
	},
	{
		targetId: 'plan-form-skill',
		title: 'Lĩnh vực',
		body: 'Chọn lĩnh vực',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 1,
		path: `/plans/${VID}/target/${PID}/create`,
	},
	{
		targetId: 'plan-form-content',
		title: 'Nội dung mục tiêu',
		body: 'Nhập nội dung mục tiêu',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 2,
	},
	{
		targetId: 'plan-form-assist',
		title: 'Chọn mức độ hỗ trợ',
		body: 'Chọn mức độ hỗ trợ',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 3,
	},
	{
		targetId: 'plan-form-reason',
		title: 'Cơ sở hợp lý',
		body: 'Nhập cơ sở hợp lý',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 4,
	},
	{
		targetId: 'plan-form-strategy',
		title: 'Chiến lược',
		body: 'Nhập chiến lược',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 5,
	},
	{
		targetId: 'plan-form-process',
		title: 'Quy trình thực hiện',
		body: 'Nhập quy trình thực hiện',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 6,
	},
	{
		targetId: 'plan-form-activity',
		title: 'Hoạt động',
		body: 'Thêm hoạt động cho mục tiêu nếu có',
		placement: 'right' as const,
		action: 'continue' as const,
		badge: 7,
	},
	{
		targetId: 'plan-form-submit',
		title: 'Hoàn thành',
		body: 'Bấm nút Thêm mục tiêu để hoàn thành',
		placement: 'right' as const,
		badge: 8,
	},
];

const TASK_SPOTLIGHTS: Record<
	string,
	{
		targetId: string;
		title: string;
		body: string;
		placement: 'right' | 'left' | 'bottom';
		action?: 'click' | 'continue';
		badge?: number;
		path?: string;
	}[]
> = {
	add_student: CREATE_STUDENT_SPOTLIGHTS,
	staff_invitation: INVITE_THERAPIST_SPOTLIGHTS,
	edit_plan_with_ai: CREATE_PLAN_SPOTLIGHTS,
	edit_goal_library: ADD_GOAL_SPOTLIGHTS,
	edit_plan: EDIT_PLAN_SPOTLIGHTS,
};

const TASK_SUCCESS_VI: Record<string, string> = {
	add_student:
		'Bạn đã hoàn thành một bước quan trọng để bắt đầu sử dụng Luca Education. Hãy tiếp tục khám phá các tính năng tiếp theo để làm quen với hệ thống.',
	staff_invitation:
		'Bạn đã hoàn thành một trong những thiết lập ban đầu của hệ thống. Lời mời tham gia vào trung tâm đã được gửi thành công, Trị liệu viên cần mở email và làm theo hướng dẫn để hoàn tất. Tiếp tục để khám phá những tính năng hữu ích của Luca Education.',
	edit_plan_with_ai:
		'Bạn đã sẵn sàng sử dụng Luca! Tiếp theo, hãy khám phá Ngân hàng mục tiêu và Kế hoạch hiện tại bằng cách thêm mục tiêu từ Thư viện nội bộ và áp dụng vào kế hoạch của học sinh.',
	edit_goal_library:
		'Bạn đã tạo mục tiêu đầu tiên thành công. Tiếp tục hoàn thành các bước còn lại để sẵn sàng triển khai kế hoạch can thiệp.',
	edit_plan:
		'Bạn đã thực hiện thành công một tác vụ trong quy trình can thiệp. Hãy tiếp tục để hoàn tất quá trình làm quen với Luca.',
	__getting_started_done__:
		'Bạn đã sẵn sàng sử dụng Luca! Tiếp theo, hãy khám phá Ngân hàng mục tiêu và Kế hoạch hiện tại bằng cách thêm mục tiêu từ Thư viện nội bộ và áp dụng vào kế hoạch của học sinh.',
};

const TASK_SUCCESS_META: Record<
	string,
	{ title: string; pauseLabel: string; confirmLabel: string }
> = {
	edit_plan_with_ai: {
		title: 'Chúc mừng!',
		pauseLabel: 'Để sau',
		confirmLabel: 'Tiếp tục khám phá',
	},
	edit_goal_library: {
		title: 'Tuyệt vời!',
		pauseLabel: 'Tạm dừng',
		confirmLabel: 'Tiếp tục',
	},
	edit_plan: {
		title: 'Hoàn thành!',
		pauseLabel: 'Tạm dừng',
		confirmLabel: 'Tiếp tục',
	},
	__getting_started_done__: {
		title: 'Chúc mừng!',
		pauseLabel: 'Để sau',
		confirmLabel: 'Tiếp tục khám phá',
	},
};

const GIFT_SESSION_KEY = 'luca_tutorial_gift_shown';

function tutorialsFromResponse(res: any): TutorialItem[] {
	const payload = res?.data?.data ?? res?.data;
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.tutorials)) return payload.tutorials;
	if (Array.isArray(payload?.data)) return payload.data;
	return [];
}

function staffRole() {
	const role = store.state.profile?.staff?.role;
	return typeof role === 'string' ? role.trim().toLowerCase() : '';
}

function tutorialNamespaces(): TutorialNamespace[] {
	return staffRole() === 'org_admin' ? ['org_admin', 'staff'] : ['staff'];
}

let tutorialNamespace: TutorialNamespace = 'staff';
let pendingLoad = false;

async function fetchTutorials(): Promise<TutorialItem[]> {
	const namespaces = tutorialNamespaces();
	for (let i = 0; i < namespaces.length; i++) {
		const namespace = namespaces[i];
		const isLast = i === namespaces.length - 1;
		try {
			const res = await $api.tutorial.list({
				platform: 'web',
				namespace,
			});
			const items = tutorialsFromResponse(res);
			tutorialNamespace = namespace;
			if (items.length || isLast || res?.data) return items;
		} catch (e) {
			console.log(e);
			const status = (e as any)?.response?.status;
			if (status === 401 || isLast) return [];
		}
	}
	return [];
}

const tutorials = ref<TutorialItem[]>([]);
const phase = ref<TutorialPhase>('idle');
const loading = ref(false);
const spotlightIndex = ref(0);
const checklistExpanded = ref(true);
const showStepSuccess = ref(false);

const selectedTaskKey = ref<string | null>(null);
const lastCompletedTaskKey = ref<string | null>(null);

const gettingStarted = computed(() =>
	tutorials.value.find((t) => t.key === 'getting_started_for_web')
);

const tuitionTutorial = computed(() =>
	tutorials.value.find(
		(t) => t.key !== 'getting_started_for_web' && t.position === 2
	) || tutorials.value[1]
);

const hasIncomplete = computed(() =>
	tutorials.value.some(
		(t) =>
			!t.completed ||
			(t.tasks || []).some((task) => !task.completed)
	)
);

const activeTask = computed(() => {
	if (selectedTaskKey.value) {
		return (
			orderedTasks().find((t) => t.key === selectedTaskKey.value) || null
		);
	}
	return (
		sortedTasks(gettingStarted.value).find((t) => !t.completed) || null
	);
});

function currentSpotlights() {
	const key = activeTask.value?.key;
	if (!key) return [];
	return TASK_SPOTLIGHTS[key] || [];
}

const activeSpotlight = computed(() => {
	if (phase.value !== 'tour') return null;
	const steps = currentSpotlights();
	if (!steps.length) return null;
	return steps[spotlightIndex.value] || null;
});

const stepSuccessMessage = computed(
	() =>
		TASK_SUCCESS_VI[lastCompletedTaskKey.value || ''] ||
		TASK_SUCCESS_VI.add_student
);

const stepSuccessMeta = computed(() => {
	const key = lastCompletedTaskKey.value || '';
	return (
		TASK_SUCCESS_META[key] || {
			title: 'Tuyệt vời!',
			pauseLabel: 'Tạm dừng',
			confirmLabel: 'Tiếp tục',
		}
	);
});

const canAccessTutorial = computed(() =>
	['leader', 'org_admin'].includes(staffRole())
);

const isTourVisible = computed(
	() =>
		canAccessTutorial.value &&
		(phase.value === 'tour' || phase.value === 'step_success')
);

const isChecklistVisible = computed(
	() =>
		canAccessTutorial.value &&
		(phase.value === 'tour' ||
			phase.value === 'paused' ||
			phase.value === 'step_success')
);

const isFabVisible = computed(
	() =>
		canAccessTutorial.value &&
		tutorials.value.length > 0 &&
		(phase.value === 'paused' || phase.value === 'done') &&
		!checklistExpanded.value
);

const isLucaAiHidden = computed(
	() =>
		canAccessTutorial.value &&
		(phase.value === 'tour' ||
			phase.value === 'step_success' ||
			phase.value === 'gift' ||
			phase.value === 'welcome' ||
			(phase.value === 'paused' && checklistExpanded.value))
);

const isCreatePlanTour = computed(
	() =>
		phase.value === 'tour' &&
		activeTask.value?.key === 'edit_plan_with_ai'
);

const isAddGoalTour = computed(
	() =>
		phase.value === 'tour' &&
		activeTask.value?.key === 'edit_goal_library'
);

const isEditPlanTour = computed(
	() =>
		phase.value === 'tour' &&
		activeTask.value?.key === 'edit_plan'
);

const isVirtualStudentTour = computed(
	() =>
		isCreatePlanTour.value ||
		isAddGoalTour.value ||
		isEditPlanTour.value
);

function taskDisplayTitle(task: TutorialTask) {
	return TASK_TITLE_VI[task.key] || task.title;
}

function sortedTasks(tutorial?: TutorialItem | null) {
	if (!tutorial?.tasks) return [];
	return [...tutorial.tasks].sort((a, b) => a.position - b.position);
}

function hasCompletedFirstTask() {
	const first = sortedTasks(gettingStarted.value)[0];
	return !!first?.completed;
}

function progress(tutorial?: TutorialItem | null) {
	const tasks = sortedTasks(tutorial);
	const done = tasks.filter((t) => t.completed).length;
	return { done, total: tasks.length };
}

function resetHidden() {
	tutorials.value = [];
	phase.value = 'idle';
	showStepSuccess.value = false;
	checklistExpanded.value = false;
}

async function load() {
	if (!canAccessTutorial.value) {
		resetHidden();
		return;
	}
	if (loading.value) {
		pendingLoad = true;
		return;
	}
	loading.value = true;
	try {
		tutorials.value = await fetchTutorials();
	} catch (e) {
		console.log(e);
		tutorials.value = [];
	} finally {
		loading.value = false;
	}

	if (pendingLoad) {
		pendingLoad = false;
		return load();
	}

	if (!canAccessTutorial.value) {
		resetHidden();
		return;
	}

	if (!tutorials.value.length) {
		phase.value = 'done';
		return;
	}

	if (!hasIncomplete.value || hasCompletedFirstTask()) {
		phase.value = 'paused';
		checklistExpanded.value = false;
		showStepSuccess.value = false;
		return;
	}

	const giftShown = sessionStorage.getItem(GIFT_SESSION_KEY);
	if (!giftShown) {
		phase.value = 'gift';
	} else if (phase.value === 'idle' || phase.value === 'done') {
		phase.value = 'welcome';
	}
}

async function refetch() {
	try {
		tutorials.value = await fetchTutorials();
	} catch (e) {
		console.log(e);
	}

	if (!hasIncomplete.value) {
		phase.value = 'done';
		showStepSuccess.value = false;
	}
}

function acceptGift() {
	sessionStorage.setItem(GIFT_SESSION_KEY, '1');
	phase.value = 'welcome';
}

async function ensureDashboardRoute() {
	if (router.currentRoute.value.path === '/dashboard') {
		return false;
	}
	phase.value = 'paused';
	await router.push('/dashboard');
	await nextTick();
	return true;
}

async function startTour() {
	selectedTaskKey.value = null;
	spotlightIndex.value = 0;
	checklistExpanded.value = true;
	await ensureDashboardRoute();
	phase.value = 'tour';
}

async function startTask(task: TutorialTask) {
	if (!task) return;
	selectedTaskKey.value = task.key;
	spotlightIndex.value = 0;
	showStepSuccess.value = false;
	checklistExpanded.value = true;
	await ensureDashboardRoute();
	phase.value = 'tour';
}

function pause() {
	phase.value = 'paused';
	checklistExpanded.value = false;
	showStepSuccess.value = false;
}

function resume() {
	selectedTaskKey.value = null;
	spotlightIndex.value = 0;
	showStepSuccess.value = false;
	checklistExpanded.value = true;
	phase.value = 'paused';
}

function goToStepIndex(idx: number) {
	const steps = currentSpotlights();
	if (idx < 0 || idx >= steps.length) return;
	spotlightIndex.value = idx;
	const next = steps[idx];
	if (next.path && router.currentRoute.value.path !== next.path) {
		router.push(next.path);
	}
}

function advanceSpotlight() {
	const steps = currentSpotlights();
	if (!steps.length) return;
	if (spotlightIndex.value < steps.length - 1) {
		goToStepIndex(spotlightIndex.value + 1);
	}
}

function continueSpotlight() {
	const steps = currentSpotlights();
	if (
		steps.length &&
		spotlightIndex.value >= steps.length - 1 &&
		steps[spotlightIndex.value]?.action === 'continue'
	) {
		completeCurrentTask();
		return;
	}
	advanceSpotlight();
}

function setSpotlightByTarget(targetId: string) {
	const idx = currentSpotlights().findIndex((s) => s.targetId === targetId);
	if (idx >= 0) goToStepIndex(idx);
}

function openAddStudentSpotlight() {
	setSpotlightByTarget('add-student-form');
}

function openAddEmployeeSpotlight() {
	setSpotlightByTarget('add-employee-form');
}

function tutorialOfTask(taskKey: string) {
	return (
		tutorials.value.find((item) =>
			(item.tasks || []).some((task) => task.key === taskKey)
		) || null
	);
}

function isLastTaskInList(taskKey: string) {
	const tasks = sortedTasks(tutorialOfTask(taskKey));
	if (!tasks.length) return false;
	return tasks.every((task) => task.key === taskKey || task.completed);
}

async function completeCurrentTask() {
	const task = activeTask.value;
	if (!task) return false;

	const taskKey = task.key;

	if (!task.completed) {
		try {
			await $api.tutorial.markDone(task.id, tutorialNamespace);
			await refetch();
		} catch (e) {
			console.log(e);
		}
	}

	selectedTaskKey.value = null;
	lastCompletedTaskKey.value = isLastTaskInList(taskKey)
		? '__getting_started_done__'
		: taskKey;

	showStepSuccess.value = true;
	phase.value = 'step_success';
	return true;
}

function orderedTasks() {
	return [
		...sortedTasks(gettingStarted.value),
		...sortedTasks(tuitionTutorial.value),
	];
}

function nextTaskAfter(taskKey: string | null) {
	const tasks = orderedTasks();
	const current = tasks.findIndex((task) => task.key === taskKey);
	return tasks.slice(current + 1).find((task) => !task.completed) || null;
}

async function continueAfterSuccess() {
	showStepSuccess.value = false;
	if (lastCompletedTaskKey.value === '__getting_started_done__') {
		const nextTask =
			orderedTasks().find((task) => !task.completed) || null;
		if (nextTask) {
			await startTask(nextTask);
			return;
		}
		phase.value = 'done';
		checklistExpanded.value = false;
		return;
	}
	const nextTask = nextTaskAfter(lastCompletedTaskKey.value);
	if (nextTask) {
		await startTask(nextTask);
		return;
	}
	if (!hasIncomplete.value) {
		phase.value = 'done';
		checklistExpanded.value = false;
		return;
	}
	pause();
}

function dismissLater() {
	pause();
}

export function useTutorial() {
	return {
		tutorials,
		phase,
		loading,
		spotlightIndex,
		checklistExpanded,
		showStepSuccess,
		gettingStarted,
		tuitionTutorial,
		hasIncomplete,
		activeTask,
		activeSpotlight,
		stepSuccessMessage,
		stepSuccessMeta,
		selectedTaskKey,
		canAccessTutorial,
		isTourVisible,
		isChecklistVisible,
		isFabVisible,
		isLucaAiHidden,
		isCreatePlanTour,
		isAddGoalTour,
		isEditPlanTour,
		isVirtualStudentTour,
		CREATE_STUDENT_SPOTLIGHTS,
		taskDisplayTitle,
		sortedTasks,
		progress,
		load,
		refetch,
		acceptGift,
		startTour,
		startTask,
		pause,
		resume,
		advanceSpotlight,
		continueSpotlight,
		setSpotlightByTarget,
		openAddStudentSpotlight,
		openAddEmployeeSpotlight,
		completeCurrentTask,
		continueAfterSuccess,
		dismissLater,
	};
}
